import { Action, Dispatch } from 'redux';

import {
  ApiRequest,
  AttachmentUtils,
  ApiRequestError,
  FeatureFlags,
  TicketUtils,
} from 'services';
import {
  AppState,
  deleteUpdateTicketDraft,
  setCommentUpdated,
  setProcessingFlag,
  startPolling,
} from 'reducers';
import { CommentResponse } from 'types';
import { TICKETS_URL } from '../appConstants/ApiRequest';

/* Types */

export interface NewCommentAction extends Action {
  readonly errorMsg?: string;
  readonly message?: string;
  readonly ticketId?: string;
  readonly isFormVisible?: boolean;
}

export interface NewComment {
  readonly _isPostingComment: boolean;
  readonly isFormVisible: boolean;
  readonly message: string;
  readonly errorMsg?: string;
}

/* Initial State */

const initialState = {
  _isPostingComment: false,
  isFormVisible: false,
  message: '',
} as NewComment;

/* Actions */

enum Actions {
  POST_COMMENT_START = 'POST_COMMENT_START',
  POST_COMMENT_SUCCESS = 'POST_COMMENT_SUCCESS',
  POST_COMMENT_ERROR = 'POST_COMMENT_ERROR',
  SET_MESSAGE = 'SET_MESSAGE',
  TOGGLE_FORM_VISIBILITY = 'TOGGLE_FORM_VISIBILITY',
  SET_ERROR_MESSAGE = 'SET_ERROR_MESSAGE',
}

const postCommentStart = (): NewCommentAction => ({
  type: Actions.POST_COMMENT_START,
});

const postCommentSuccess = (): NewCommentAction => ({
  type: Actions.POST_COMMENT_SUCCESS,
});

const postCommentError = (errorMsg: string): NewCommentAction => ({
  type: Actions.POST_COMMENT_ERROR,
  errorMsg,
});

const setMessage = (message: string): NewCommentAction => ({
  type: Actions.SET_MESSAGE,
  message: message,
});

const formVisibility = (isFormVisible: boolean): NewCommentAction => ({
  type: Actions.TOGGLE_FORM_VISIBILITY,
  isFormVisible,
});

const setErrorMessage = (errorMessage: string): NewCommentAction => ({
  type: Actions.SET_ERROR_MESSAGE,
  errorMsg: errorMessage,
});

/* Reducer */

const newCommentReducer = (state = initialState, action: NewCommentAction) => {
  switch (action.type) {
    case Actions.SET_MESSAGE:
      return {
        ...state,
        message: action.message,
      };
    case Actions.POST_COMMENT_START:
      return {
        ...state,
        _isPostingComment: true,
      };
    case Actions.POST_COMMENT_SUCCESS:
      return {
        ...state,
        _isPostingComment: false,
      };
    case Actions.POST_COMMENT_ERROR:
      return {
        ...state,
        _isPostingComment: false,
        errorMsg: action.errorMsg,
      };
    case Actions.TOGGLE_FORM_VISIBILITY:
      return {
        ...state,
        isFormVisible: action.isFormVisible,
      };
    case Actions.SET_ERROR_MESSAGE:
      return {
        ...state,
        errorMsg: action.errorMsg,
      };
    default:
      return state;
  }
};

/* Thunks */

const postComment = (
  ticketId: string,
  message: string,
  shouldStartPolling = false,
) => (dispatch: Dispatch<NewCommentAction>, getState: Function) => {
  dispatch(postCommentStart());

  const state: AppState = getState();
  const items = state.attachments.items;
  const attachments = AttachmentUtils.forCommentPost(items);
  /*
   * You cannot have an empty message for a comment so if submitting just an
   * attachment default to `File attached without comment.`
   */
  const attachmentOnlyMessage =
    AttachmentUtils.asArray(items).length > 0 && message === ''
      ? 'File attached without comment.'
      : '';

  const payload = {
    comment: {
      text: message || attachmentOnlyMessage,
      attachments,
    },
  };

  const onError = (error: ApiRequestError) => {
    const errorMessage =
      error && error.message ? error.message : 'Error creating comment';
    dispatch(postCommentError(errorMessage));
  };

  const onSuccess = (response: CommentResponse) => {
    if (FeatureFlags.isActive('ticket-polling-on-update')) {
      TicketUtils.setPollingTicketList(ticketId, response.eventId);
    }

    dispatch(postCommentSuccess());
    dispatch(setMessage(''));
    dispatch(setErrorMessage(''));
    dispatch(deleteUpdateTicketDraft(ticketId));
    dispatch(setCommentUpdated(true));
    if (shouldStartPolling) {
      dispatch(setProcessingFlag(true));
      dispatch(formVisibility(false));
      dispatch(startPolling());
    }
  };

  return ApiRequest.post(
    TICKETS_URL + `/${ticketId}/comments`,

    payload,
    {
      headers: {
        'x-async': 'true',
      },
      onError,
      onSuccess,
    },
  );
};

export {
  newCommentReducer,
  postComment,
  setMessage,
  setErrorMessage,
  formVisibility,
};
